import { Box, FormControlLabel, Switch, Typography } from '@mui/material';
import React, { Fragment, ReactElement, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { CobranderSelector } from 'app/components/form-controls/cobrander-selector';
import { StorePicker } from 'app/components/store-picker';
import { makeSeamPattern } from 'app/model';
import { Store } from 'app/model/cris/store';
import { searchService } from 'app/services/search-service';
import { storeService } from 'app/services/store.service';
import { PreProdPattern } from 'app/type';
import { selectMidFilters } from 'features/tuples/tuple.slice';

import { PatternForm } from '../seam/request/pattern-form';

export const TupleExplorer = (): ReactElement => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const [correction, setCorrection] = React.useState(false);
    const [data, setData] = React.useState(makeSeamPattern);
    const [store, setStore] = React.useState<Store | undefined>();
    const filters = useSelector(selectMidFilters);

    useEffect(() => {
        if (queryParams.has('store')) {
            storeService
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                .get(queryParams.get('store')!)
                .subscribe((newStore) => {
                    setStore(newStore);
                });

            const preProdPattern = [...queryParams.entries()].reduce<Record<string, string[] | string>>(
                (acc, [key, value]) => {
                    if ('store' === key) {
                        return acc;
                    } else if (Array.isArray(acc[key])) {
                        (acc[key] as string[]).push(value);
                    } else if (acc[key]) {
                        // if acc[key] has already a value, then change value type to array and add new value
                        acc[key] = [acc[key] as string, value];
                    } else {
                        acc[key] = value;
                    }

                    return acc;
                },
                {},
            );

            const seamPatternData = makeSeamPattern(preProdPattern as unknown as PreProdPattern).data;

            const nonFalsySeamPatternData = Object.entries(seamPatternData).reduce<Record<string, unknown>>(
                (acc, [key, value]) => {
                    if (value) {
                        acc[key] = value;
                    }

                    return acc;
                },
                {},
            );

            // $ and ^ should be removed from the ends any of the filled in pattern values
            const patterns = [
                'cityPattern',
                'countryPattern',
                'descPattern',
                'extractPattern',
                'mccPattern',
                'midPattern',
                'statePattern',
                'zipPattern',
            ] as const;
            patterns.forEach((key) => {
                if (Array.isArray(nonFalsySeamPatternData[key])) {
                    nonFalsySeamPatternData[key] = (nonFalsySeamPatternData[key] as string[]).map((v) =>
                        '$' === v.slice(-1) || '^' === v.slice(-1) ? v.slice(0, -1) : v,
                    );
                } else if ('string' === typeof nonFalsySeamPatternData[key]) {
                    const lastChar = (nonFalsySeamPatternData[key] as string).slice(-1);
                    if ('$' === lastChar || '^' === lastChar) {
                        nonFalsySeamPatternData[key] = (nonFalsySeamPatternData[key] as string).slice(0, -1);
                    }
                }
            });

            setData((currentData) => {
                return {
                    ...currentData,
                    data: {
                        ...currentData.data,
                        ...nonFalsySeamPatternData,
                    },
                };
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Fragment>
            <Typography variant='h4'>Tuple Explorer</Typography>
            <Typography variant='body1' className='mb-2'>
                Find exising tuples for arbitrary stores/brands. Start typing store name to show matching results.
            </Typography>
            <Box display='flex' className='my-2' alignItems='center'>
                <Box>
                    <FormControlLabel
                        control={
                            <Switch checked={correction} onChange={(event) => setCorrection(event.target.checked)} />
                        }
                        label={correction ? 'Show Mapped' : 'Show Unmapped'}
                    />
                </Box>
                <Box flexGrow={1}>
                    <StorePicker size='small' value={store} onChange={(event, newStore) => setStore(newStore)} />
                </Box>
                <CobranderSelector
                    value={data.data.cobrander}
                    sx={{ minWidth: '15em' }}
                    size='small'
                    variant='outlined'
                    className='ml-1'
                    onChange={(evt) =>
                        setData((currentData) => {
                            return {
                                ...currentData,
                                data: {
                                    ...currentData.data,
                                    cobrander: evt.target.value,
                                },
                            };
                        })
                    }
                />
            </Box>
            <PatternForm
                data={data}
                correction={correction}
                hideActions={true}
                onChange={(newData) => setData(newData)}
                onSubmit={() => {
                    // noop
                }}
                onCancel={() => {
                    // noop
                }}
                onSearch={(opts, searchAfter) =>
                    searchService.findTuples(
                        data.data,
                        filters.filters,
                        correction ? '1' : '0',
                        store?.id,
                        searchAfter,
                        opts,
                    )
                }
                store={store}
            />
        </Fragment>
    );
};
